import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { AuthUser, LoginCredentials } from '../types';
import { db } from '../services/database';
import { supabase } from '../services/supabase';

interface AuthContextType {
  user: AuthUser | null;
  admin: AuthUser | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  adminLogin: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;
  adminLogout: () => void;
  register: (data: { cedula: string; email: string; fullName: string; password: string }) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [admin, setAdmin] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMountedRef = useRef(true);

  // Función para verificar si el componente sigue montado
  const updateStateIfMounted = useCallback((updater: () => void) => {
    if (isMountedRef.current) {
      updater();
    }
  }, []);

  // Verificar autenticación al cargar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Verificar sesión actual de Supabase
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          const dbUser = await db.getUserById(session.user.id);
          
          if (dbUser && dbUser.is_active && !dbUser.is_banned) {
            if (dbUser.role === 'admin') {
              updateStateIfMounted(() => setAdmin({
                id: dbUser.id,
                email: dbUser.email,
                fullName: dbUser.full_name,
                isAdmin: true
              }));
            } else {
              updateStateIfMounted(() => setUser({
                id: dbUser.id,
                email: dbUser.email,
                fullName: dbUser.full_name,
                isAdmin: false
              }));
            }
          } else {
            await supabase.auth.signOut();
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        await supabase.auth.signOut();
      } finally {
        updateStateIfMounted(() => setIsLoading(false));
      }
    };

    checkAuth();

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        updateStateIfMounted(() => {
          setUser(null);
          setAdmin(null);
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [updateStateIfMounted]);

  // Login para usuarios normales
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      // Verificar que NO sea el admin
      if (credentials.email === 'admin@castle') {
        return false;
      }

      const authenticatedUser = await db.authenticateUser(credentials.email, credentials.password);
      
      if (authenticatedUser && authenticatedUser.role !== 'admin') {
        const authUser: AuthUser = {
          id: authenticatedUser.id,
          email: authenticatedUser.email,
          fullName: authenticatedUser.full_name,
          isAdmin: false
        };
        
        updateStateIfMounted(() => setUser(authUser));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }, [updateStateIfMounted]);

  // Login separado para administradores
  const adminLogin = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      // Solo permitir login de admin con email específico
      if (credentials.email !== 'admin@castle') {
        return false;
      }

      const authenticatedAdmin = await db.authenticateUser(credentials.email, credentials.password);
      
      if (authenticatedAdmin && authenticatedAdmin.role === 'admin') {
        const authAdmin: AuthUser = {
          id: authenticatedAdmin.id,
          email: authenticatedAdmin.email,
          fullName: authenticatedAdmin.full_name,
          isAdmin: true
        };
        
        updateStateIfMounted(() => setAdmin(authAdmin));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Admin login error:', error);
      return false;
    }
  }, [updateStateIfMounted]);

  // Logout para usuarios normales
  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    updateStateIfMounted(() => setUser(null));
  }, [updateStateIfMounted]);

  // Logout para administradores
  const adminLogout = useCallback(async () => {
    await supabase.auth.signOut();
    updateStateIfMounted(() => setAdmin(null));
  }, [updateStateIfMounted]);

  // Registro (solo para usuarios normales)
  const register = useCallback(async (data: { cedula: string; email: string; fullName: string; password: string }): Promise<boolean> => {
    try {
      // Verificar que no sea email de admin
      if (data.email === 'admin@castle') {
        return false;
      }

      const existingUser = await db.getUserByEmail(data.email);
      if (existingUser) return false;

      const newUser = await db.createUser(data);
      if (!newUser) return false;

      const authUser: AuthUser = {
        id: newUser.id,
        email: newUser.email,
        fullName: newUser.full_name,
        isAdmin: false
      };
      
      updateStateIfMounted(() => setUser(authUser));
      return true;
    } catch (error) {
      console.error('Register error:', error);
      return false;
    }
  }, [updateStateIfMounted]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const value = {
    user,
    admin,
    isLoading,
    login,
    adminLogin,
    logout,
    adminLogout,
    register
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
